// 型定義 — スコープ確定書 §4 に準拠
// すべての永続化対象データ構造をここで定義する。

export type RouletteId = string; // UUID v4
export type SetId = string; // UUID v4

export type ProbMode = 'default' | 'fixed' | 'weight';
export type SpecialRole = 'target' | 'avoid';

/** 特殊演出の種別 */
export type EffectKey =
  // 音系 (グループ内排他)
  | 'soundConfirm'      // 確定音 (target当選)
  | 'soundExpect'       // 期待度アップ音
  | 'soundNgConfirm'    // NG確定音 (avoid当選)
  // ミニキャラ系 (グループ内排他、ホイール周辺で動く小キャラ)
  | 'miniCharTarget'    // ミニドラゴン
  | 'miniCharExpect'    // ミニウサギ
  | 'miniCharNg'        // ミニゴースト
  // カットイン系 (グループ内排他、画面横切り)
  | 'cutInTarget'       // ドラゴンカットイン
  | 'cutInExpect'       // ウサギカットイン
  | 'cutInNg'           // ゴーストカットイン
  // その他系 (各々独立)
  | 'flashConfirm'      // 画面フラッシュ
  | 'wheelGlow'         // ホイール外枠を虹色に発光
  | 'textChance';       // 「CHANCE!」テキストポップアップ

/** 0.0-1.0 の範囲値 (min ≦ max)。スピン時に [min, max] の uniform random で実効値を決定 */
export interface RangeValue {
  min: number;
  max: number;
}

export interface EffectConfig {
  enabled: boolean;
  /** 発火確率の範囲。スピン時に min〜max からランダム抽選した値で抽選通過判定 */
  probabilityRange: RangeValue;
  /** true なら probabilityRange を無視し、毎スピン 0〜100% から完全ランダムで抽選 */
  probabilityRandom: boolean;
  /** スピン開始からの発火タイミング比率の範囲。スピン時に min〜max でランダム決定 */
  timingRange: RangeValue;
  /** true なら timingRange を無視し、毎スピン 0〜100% から完全ランダムで決定 */
  timingRandom: boolean;
  /** バリエーション選択。
   *  0  = ランダム (1〜5 から発火時に抽選)
   *  1〜5 = 固定 */
  selectedVariant: number;
}

export interface SpinEffectSettings {
  /** マスター ON/OFF */
  enabled: boolean;
  effects: Record<EffectKey, EffectConfig>;
}

export type SpinSoundKey = 'snap' | 'click' | 'drum' | 'coin' | 'soft' | 'mute';
export type WinSoundKey = 'bell' | 'fanfare' | 'casino' | 'chime' | 'victory' | 'mute';

export type TextOrientation = 'radial' | 'tangent' | 'horizontal';
export type TextSizeMode = 'auto' | 'fixed';
/** セグメント内のテキスト寄せ。'outer'=外側ぎりぎり (Windows版風) / 'center'=半径方向の中央 */
export type TextAlign = 'center' | 'outer';
export type MarkerPosition = 'top' | 'right' | 'bottom' | 'left' | 'free';
export type MarkerStyle = 'overlap' | 'outside' | 'inside';
export type LayoutMode = 'standard' | 'random';
/** 回転方向 / セグメント並び方向の共通型 */
export type Direction = 'cw' | 'ccw';
/** テーマモード (アプリ全体共通設定) */
export type ThemeMode = 'auto' | 'light' | 'dark';

export interface Item {
  text: string;
  enabled: boolean;
  probMode: ProbMode;
  /** fixed: 0<v<100 ／ weight: 0.25..N（0.5刻み）／ default: 未使用 */
  probValue?: number;
  /** 1..10 — ホイール上での区画分割数 */
  splitCount: number;
  /** カスタムセグメント色 (CSS color, 例 '#ff0000')。未設定ならパレット既定 */
  customColor?: string;
  /** 当てたい (target) / 当てたくない (avoid) の指定。未設定は通常 */
  specialRole?: SpecialRole;
}

export interface ItemSet {
  id: SetId;
  name: string;
  items: Item[];
}

/** スピン速度プロファイルの曲線タイプ。
 *  - linear:    直線変化
 *  - easeOut:   急変化 → 緩く尾を引く (例: 投げてから自然に減速する感じ)
 *  - easeIn:    緩く始まり → 急変化 (例: ゆっくり立ち上がってからキレよく)
 *  - easeInOut: S字 (両端緩やか、中央急)
 */
export type SpinCurve = 'linear' | 'easeOut' | 'easeIn' | 'easeInOut';

/** プリセットプロファイル — 各 duration の既定 4 段階比率 / peak RPS を切替可能。
 *  - x: 全 duration 共通比率 + 共通 peak (4 RPS) — シンプル定数モデル
 *  - y: 全 duration 共通比率 + duration 連動 peak — 短時間ほど速く
 *  - z: duration ごとに比率調整 + 連動 peak — 個性化モデル
 */
export type SpinPresetProfile = 'x' | 'y' | 'z';

/** スピン 4 段階モデルの個別調整値。null/未設定フィールドはプリセット値を使用。
 *  時間は ms 単位、速度は RPS (回転/秒) 単位。
 *
 *  時間境界 (前後を侵犯しないよう実行時にクランプ):
 *    - pushEndMs:   ① 加速の終了 (= ② 巡航の開始)
 *    - cruiseEndMs: ② 巡航の終了 (= ③ 減速の開始)
 *    - decelEndMs:  ③ 減速の終了 (= ④ 着地の開始)
 *    - ④ 着地の終了は spinDurationMs で固定
 *
 *  速度境界 (各段階の終端速度):
 *    - pushEndRps:   ① 加速の終端 (= ② 巡航の開始速度) ※① の開始は 0 固定
 *    - cruiseEndRps: ② 巡航の終端 (= ③ 減速の開始速度)
 *    - decelEndRps:  ③ 減速の終端 (= ④ 着地の開始速度) ※④ の終了は 0 固定
 *
 *  曲線タイプ:
 *    - pushCurve / cruiseCurve / decelCurve / landingCurve
 */
export interface SpinPhaseOverrides {
  pushEndMs?: number;
  cruiseEndMs?: number;
  decelEndMs?: number;
  pushEndRps?: number;
  cruiseEndRps?: number;
  decelEndRps?: number;
  pushCurve?: SpinCurve;
  cruiseCurve?: SpinCurve;
  decelCurve?: SpinCurve;
  landingCurve?: SpinCurve;
}

export interface RouletteSettings {
  spinDurationMs: number;
  /** スピン終了時間のランダム化割合 (0-1, スピン秒数に対する割合)。0 = ランダム化なし。
   *  > 0 の場合、各スピンで spinDurationMs × ±ratio の delta を加算。
   *  delta < 0: 巡航 → 着地の順に短縮 (急停止演出)
   *  delta > 0: 着地を延長 (「まだ止まらない?」演出)
   *  例: ratio=0.3 で spin 5秒 → ±1.5秒 の振れ幅 → 3.5〜6.5秒のいずれか
   */
  spinDurationRandomRatio: number;
  /** プリセットプロファイル ('x' / 'y' / 'z')。default 'z' (個性化モデル)。 */
  spinPresetProfile: SpinPresetProfile;
  /** true 時、毎スピンで X/Y/Z から均等抽選 (spinPresetProfile は無視)。 */
  spinPresetRandom: boolean;
  /** true 時、毎スピンで 1/3/5/7/9/12 秒のプリセットから均等抽選 (spinDurationMs は無視)。 */
  spinDurationRandom: boolean;
  /** 4 段階モデルの個別上書き値。null = プリセット値そのまま。 */
  spinPhaseOverrides: SpinPhaseOverrides | null;
  /** スピン詳細ランダム化の強度 (0-1)。0 = ランダムなし。
   *  > 0 の場合、スピンごとに 4 段階の終了時間と速度を ±intensity × 基準値で揺らす。
   *  総時間は維持 (= spinDurationMs と独立)。 */
  spinPhaseRandomize: number;
  doubleStopWindowMs: number;
  tripleStopWindowMs: number;
  spinSound: SpinSoundKey;
  decideSound: WinSoundKey;
  textOrientation: TextOrientation;
  textSizeMode: TextSizeMode;
  /** textSizeMode === 'fixed' のときのみ使用。8〜32px */
  textFixedSize: number;
  /** テキスト寄せ位置 (radial 向き時のみ有効)。デフォルト 'outer' */
  textAlign: TextAlign;
  markerPosition: MarkerPosition;
  /** マーカー描画スタイル: overlap=ホイール内側に少し被る (Windows風) / outside=完全外側 */
  markerStyle: MarkerStyle;
  /** markerPosition === 'free' のときのみ有効。0..360 */
  markerAngleDeg?: number;
  layoutMode: LayoutMode;
  randomEachSpin: boolean;
  /** ホイール回転方向。'cw'=時計回り (デフォルト) / 'ccw'=反時計回り */
  spinDirection: Direction;
  /** セグメント並び方向。'cw'=12時から時計回りに配置 (デフォルト) / 'ccw'=反時計回り */
  segmentDirection: Direction;
  /** 特殊演出設定 */
  spinEffects: SpinEffectSettings;
}

/** アプリ全体共通の設定 (個別ルーレットには紐づかない) */
export interface AppPreferences {
  /** テーマ */
  themeMode: ThemeMode;
  /** 音量 (0..100) — 全ルーレット共通 */
  volume: number;
  /** 項目リストに各項目の現在確率 (%) を表示するか */
  showItemProbability: boolean;
  /** 項目リストに各項目の当選回数 (履歴ベース) を表示するか */
  showItemWinCount: boolean;
  /** ランダム配置時、分割の均等配置を維持するか。
   *  true  = 項目順だけシャッフル、各項目の splits は均等分散を維持
   *  false (デフォルト) = 各 split を独立にランダム配置 (Windows版同等) */
  randomMaintainEvenness: boolean;
}

export interface WinRecord {
  /** ISO 8601 datetime */
  spinAt: string;
  text: string;
  setId: SetId;
}

export interface Roulette {
  id: RouletteId;
  name: string;
  sets: ItemSet[];
  currentSetId: SetId;
  settings: RouletteSettings;
  winHistory: WinRecord[];
  /** グリッド表示するか。false で非表示。最低 1 件は visible である必要がある。 */
  visible: boolean;
}

/** 一時シャッフル順 (Windows版 _pre_shuffle_entries 相当)。 */
export interface ShuffleOrder {
  setId: SetId;
  /** 'item' = 項目順だけシャッフル、splits は buildLayout が均等分散
   *  'split' = 各 split を独立にランダム配置 (項目本体不変、order に重複あり) */
  mode: 'item' | 'split';
  /** 'item': 項目インデックス配列 (重複なし、長さ = items.length)
   *  'split': 項目インデックス配列 (重複あり、各項目が splitCount 回出現) */
  order: number[];
}

/** UI の一時状態（折りたたみ・シャッフル順等）。 */
export interface UiState {
  itemListCollapsed: boolean;
  /** 「今すぐシャッフル」で適用された一時順序。null/未定義は標準順 */
  shuffleOrder: ShuffleOrder | null;
}

export interface AppState {
  schemaVersion: number;
  roulettes: Roulette[];
  activeRouletteId: RouletteId;
  ui: UiState;
  /** アプリ全体共通設定 (テーマ・音量・表示切替・ランダム挙動) */
  preferences: AppPreferences;
}

/** 現在の永続化スキーマバージョン。互換破壊変更時にインクリメントする。 */
export const SCHEMA_VERSION = 1;
